import { isEmpty, isEqual } from 'lodash'
import Vue from 'vue'
import Router from 'vue-router'
import i18n from '@/i18n'
import store from '@/store/store'

let lastLocale = ''
const defaultListType = 'map'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/:locale(de|en|da)?',
      component: {
        beforeRouteEnter: redirectOrSetLocale,
        beforeRouteUpdate: redirectOrSetLocale,
        /* router-view with keep-alive for child routes */
        render(h) { return h('keep-alive', [h('router-view')]) }
      },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('./views/Dashboard.vue'),
          meta: {
            title: 'Start'
          }
        },
        {
          path: 'about',
          name: 'about',
          component: () => import('./views/About.vue'),
          meta: {
            title: 'Informationen'
          }
        },
        {
          path: 'institutions',
          name: 'institutions',
          component: () => import('./views/Institutions.vue'),
          props: true,
          redirect: 'institutions/map',
          children: [
            {
              path: ':listType(map)',
              name: 'map',
              component: () => import('./components/common/Map.vue'),
              props: true,
              meta: {
                title: 'Karte',
                parent: 'dashboard'
              }
            },
            {
              path: ':listType(list)',
              name: 'list',
              component: () => import('./components/institutions/List.vue'),
              props: true,
              meta: {
                title: 'Liste',
                parent: 'dashboard'
              }
            }
          ]
        },
        {
          path: 'institutions/:listType/filters',
          name: 'filters',
          component: () => import('./views/Filters.vue'),
          props: true,
          meta: {
            title: 'Filter'
          }
        },
        {
          path: 'institutions/:listType?/details/:id',
          alias: 'i/:id',
          name: 'details',
          component: () => import('./views/details/Details.vue'),
          props: true,
          meta: {
            title: 'Details'
          }
        },
        {
          path: 'institutions/:listType/details/:id/map',
          name: 'detailsMap',
          component: () => import('./views/details/Map.vue'),
          props: true,
          meta: {
            title: 'Karte',
            parent: 'details'
          }
        },
        {
          path: 'institutions/:listType?/details/:id/living-images',
          alias: ['li/:id', 'ar/:id'],
          name: 'livingImagesViewer',
          component: () => import('./views/details/LivingImagesViewer.vue'),
          props: true,
          meta: {
            title: 'Living Image Viewer',
            windowRedirectAfter: true
          }
        },
        {
          path: 'institutions/:listType?/details/:id/about',
          name: 'detailsAbout',
          component: () => import('./views/About.vue'),
          meta: {
            title: 'Informationen',
            parent: 'details'
          }
        }
      ]
    },
    {
      name: '404',
      path: '*',
      redirect: '/de'
    }
  ]
})

router.onReady(async () => {
  await waitForStorageToBeReady()

  setInitialFilterStateFromUrl()

  startListeningToRouteChanges()
})

const waitForStorageToBeReady = async (to, from, next) => {
  await store.restored
}

const setInitialFilterStateFromUrl = () => {
  store.dispatch('filters/updateState', queryStringsToState(router.currentRoute.query))
}

/*
 * Add filter query parameters from store to url/route on every navigation.
 * So not every link has to include the current query parameters...
 */
const startListeningToRouteChanges = () => {
  router.beforeEach(async (to, from, next) => {
    // next if store filter matches query
    if (isEqual(to.query, store.getters['filters/getFilterQuery'])) next()
    else {
      // Set new filter state if query is present
      if (!isEmpty(to.query)) await store.dispatch('filters/updateState', queryStringsToState(to.query))
      // Append current filter query to url
      next({ path: to.path, query: store.getters['filters/getFilterQuery'] })
    }
  })
}

/*
 * Transform comma separated filters from query strings
 * to state object with arrays.
 */
const queryStringsToState = (query = {}) => {
  const {
    isFavorite = '',
    categories = '',
    tags = '',
    searchQuery = ''
  } = query

  return {
    isFavorite: isFavorite === 'true',
    categories: categories.length ? categories.split(',') : [],
    tags: tags.length ? tags.split(',') : [],
    searchQuery
  }
}

// After navigating back from Living Images Viewer the app needs to reload, because of the resize event listener by AR.js
router.beforeEach((to, from, next) => {
  // this will recursively go up from child to parent, useful if utilizing children routes. otherwise, you don't need it
  let matchedObject = from.matched.slice().reverse().find(r => r.meta && r.meta.hasOwnProperty('windowRedirectAfter'))

  if (matchedObject) {
    if (matchedObject.meta.windowRedirectAfter === true) {
      window.location.href = to.fullPath
      return
    }
  }
  next()
})

/* Reirect missing locale and alias URLs */
function redirectOrSetLocale(to, from, next) {
  let { locale, listType } = to.params

  if (!locale) {
    /* Set missing list type for short URLs */
    if (to.params.hasOwnProperty('listType') && !listType) {
      to.params.listType = defaultListType
    }

    to.params.locale = 'de'

    /* Routes by name, so alias routes are resolved to their full route */
    next(to)
    return
  }

  // Set locale and fetch new localized data
  if (locale !== lastLocale) {
    i18n.locale = lastLocale = locale
    store.dispatch('institutions/fetchInstitutions')
  }

  next()
}

export default router
